import React, { ChangeEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import {
  StyledCheckoutDrawer,
  StyledDrawerBody,
  StyledDrawerHeader,
  StyledClinButton,
  StyledClinPrimaryButton2,
  StyledLoadingContainer,
  StyledLoadingSpinner,
  StyledRow,
  StyledColumn
} from './ShippingAddressDrawer.styles'
import { ClinText } from '../../components/ClinText'
import { ClinGroup } from '../../components/ClinGroup'
import { ClinTheme } from '../../ClinTheme'
import { ClinSpinner } from '../../components/ClinSpinner'
import { CountryDto, CreateShippingAddressDto } from '../../types/swaggerTypes'
import {
  getInputValidation,
  ValidationType
} from '../../utils/Forms/getInputValidation'
import { number, object, string } from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import { TypographyVariant } from '../../components/ClinText/ClinText.styles'
import { ClinSpacer } from '../../components/ClinSpacer'
import { ClinTextInput } from '../../components/ClinTextInput'
import { ClinSelect } from '../../components/ClinSelect'
import {
  getInputPlaceholder,
  PlaceholderType
} from '../../utils/Forms/getInputPlaceholder'
import { ClinIcon } from '../../components/ClinIcon'
import { ClinIconPathName } from '../../components/ClinIcon/ClinIcon.paths'
import { ClinButton } from '../../components/ClinButton'

interface IShippingAddressDrawerProps {
  /** Handle closing the confirmation */
  handleClose?: () => void
  /** Displays an error notification **/
  hasError?: boolean
  /** Displays error message **/
  errorMessage?: string
  /** Whether the submission is still processing **/
  isSubmitting: boolean
  /** List of countries **/
  countries: CountryDto[]
  resetOnSubmit?: boolean
  selectedCountry?: string
  addressModalError?: string
  /** reset form manually - for be validation **/
  resetForm?: boolean
  /** Handle confirmation of dissociation  **/
  handleFormSubmit: (data: CreateShippingAddressDto) => void
}

const defaultState: CreateShippingAddressDto = {
  address: {
    addressId: 0,
    address1: '',
    address2: '',
    address3: '',
    address4: '',
    city: '',
    postalCode: '',
    state: '',
    country: ''
  }
}

export const ShippingAddressDrawer: React.FC<IShippingAddressDrawerProps> = (
  props
) => {
  const {
    countries,
    isSubmitting,
    selectedCountry,
    addressModalError,
    resetForm,
    resetOnSubmit,
    handleFormSubmit,
    handleClose
  } = props
  const { t } = useTranslation()

  const requiredText = getInputValidation(ValidationType.RequiredField)

  const NewShippingAddressSchema = object().shape({
    address: object().shape({
      addressId: number(),
      address1: string().required(requiredText),
      address2: string().required(requiredText),
      address3: string(),
      address4: string(),
      city: string().required(requiredText),
      postalCode: string().required(requiredText),
      state: string().required(requiredText),
      country: string().required(requiredText)
    })
  })

  const {
    register,
    control,
    reset,
    setValue,
    clearErrors,
    handleSubmit,
    formState: { errors }
  } = useForm<CreateShippingAddressDto>({
    defaultValues: defaultState,
    resolver: yupResolver(NewShippingAddressSchema)
  })

  const onSubmit = handleSubmit((data: CreateShippingAddressDto) => {
    handleFormSubmit(data)
    clearErrors()
    resetOnSubmit && reset({})
  })

  const onCancel = () => {
    if (handleClose) {
      handleClose()
    }
    reset({})
  }
  useEffect(() => {
    if (selectedCountry) {
      setValue('address.country', selectedCountry)
    }
  }, [selectedCountry, setValue])

  useEffect(() => {
    if (resetForm) {
      reset(defaultState)
    }

    return () => {}
  }, [resetForm])

  return (
    <StyledCheckoutDrawer>
      <StyledDrawerHeader>
        <ClinButton variant="linkWithIcon" onClick={handleClose}>
          <ClinIcon
            style={{
              marginRight: ClinTheme.space[2]
            }}
            iconSize={ClinTheme.fontSizes[3]}
            iconName={ClinIconPathName.ArrowLeft}
          />
          <div>{t('common:buttons.back_button_text')}</div>
        </ClinButton>

        <ClinSpacer height={ClinTheme.space[3]} />

        <ClinGroup justifyContent={'space-between'}>
          <ClinText
            as={'div'}
            fontWeight={ClinTheme.fontWeights.medium}
            fontSize={ClinTheme.fontSizes[3]}
            color={ClinTheme.colors.black}
            lineHeight={ClinTheme.lineHeights.heading[1]}
          >
            {t('request_new_shipping_address:drawer_title')}
          </ClinText>
        </ClinGroup>
      </StyledDrawerHeader>
      <StyledDrawerBody>
        <form onSubmit={onSubmit}>
          <ClinText
            fontWeight={ClinTheme.fontWeights.normal}
            fontSize={ClinTheme.fontSizes[2]}
          >
            {t('request_new_shipping_address:mandatory_message')}
          </ClinText>

          <ClinSpacer height={ClinTheme.space[3]} />

          <div style={{ display: 'none' }}>
            <ClinTextInput
              id="address-id"
              {...register(`address.addressId`)}
              value={0}
            />
          </div>

          <ClinTextInput
            id="address"
            {...register(`address.address1`)}
            label={t('request_new_shipping_address:form_label_hospital')}
            width="100%"
            labelFontWeight={ClinTheme.fontWeights.normal}
            hasError={!!(errors && errors.address?.address1)}
            prompt={errors.address?.address1?.message}
          />

          <ClinSpacer height={ClinTheme.space[3]} />

          <ClinTextInput
            id="address-one"
            {...register(`address.address2`)}
            label={t('request_new_shipping_address:form_label_address_line_1')}
            width="100%"
            labelFontWeight={ClinTheme.fontWeights.normal}
            hasError={!!(errors && errors.address?.address2)}
            prompt={errors.address?.address2?.message}
          />

          <ClinSpacer height={ClinTheme.space[3]} />

          <ClinTextInput
            id="address-two"
            {...register(`address.address3`)}
            label={t('request_new_shipping_address:form_label_address_line_2')}
            width="100%"
            labelFontWeight={ClinTheme.fontWeights.normal}
            hasError={!!(errors && errors.address?.address3)}
            prompt={errors.address?.address3?.message}
          />

          <ClinSpacer height={ClinTheme.space[3]} />

          <ClinTextInput
            id="address-three"
            {...register(`address.address4`)}
            label={t('request_new_shipping_address:form_label_address_line_3')}
            width="100%"
            labelFontWeight={ClinTheme.fontWeights.normal}
            hasError={!!(errors && errors.address?.address4)}
            prompt={errors.address?.address4?.message}
          />

          <ClinSpacer height={ClinTheme.space[3]} />

          <StyledRow>
            <StyledColumn width={216}>
              <ClinTextInput
                id="city"
                {...register(`address.city`)}
                label={t('request_new_shipping_address:form_label_city')}
                width="100%"
                labelFontWeight={ClinTheme.fontWeights.normal}
                hasError={!!(errors && errors.address?.city)}
                prompt={errors.address?.city?.message}
              />
            </StyledColumn>
            <StyledColumn width={216}>
              <ClinTextInput
                id="city"
                {...register(`address.state`)}
                label={t('request_new_shipping_address:form_label_state')}
                width="100%"
                labelFontWeight={ClinTheme.fontWeights.normal}
                hasError={!!(errors && errors.address?.state)}
                prompt={errors.address?.state?.message}
              />
            </StyledColumn>
          </StyledRow>

          <ClinSpacer height={ClinTheme.space[3]} />

          <StyledRow>
            <StyledColumn width={292}>
              <Controller
                name={'address.country'}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ClinSelect
                    id="country"
                    label={t('request_new_shipping_address:form_label_country')}
                    width="100%"
                    value={value}
                    defaultValue={selectedCountry}
                    disabled={selectedCountry ? true : false}
                    onChange={(changeValue: ChangeEvent<HTMLSelectElement>) =>
                      onChange(changeValue.currentTarget.value)
                    }
                    hasError={!!(errors && errors.address?.country)}
                    prompt={errors.address?.country?.message}
                  >
                    <option value="" disabled={true}>
                      {getInputPlaceholder(PlaceholderType.SelectInput)}
                    </option>
                    {countries
                      .sort((a, b) =>
                        a.countryName && b.countryName
                          ? a.countryName.localeCompare(b.countryName)
                          : -1
                      )
                      .map((country: CountryDto, index: number) => {
                        return (
                          <option
                            key={`country-codes-${index}`}
                            value={country.countryCode}
                          >
                            {country.countryName}
                          </option>
                        )
                      })}
                  </ClinSelect>
                )}
              />
              {selectedCountry && (
                <ClinText marginTop={'5px'} variant={TypographyVariant.SmallUI}>
                  {t(
                    'request_new_shipping_address:disabled_country_warning_text'
                  )}
                </ClinText>
              )}
            </StyledColumn>
            <StyledColumn width={140}>
              <ClinTextInput
                id="postal-code"
                {...register(`address.postalCode`)}
                label={t('request_new_shipping_address:form_label_postcode')}
                width="100%"
                labelFontWeight={ClinTheme.fontWeights.normal}
                hasError={!!(errors && errors.address?.postalCode)}
                prompt={errors.address?.postalCode?.message}
              />
            </StyledColumn>
          </StyledRow>

          {addressModalError && (
            <>
              <ClinSpacer height={ClinTheme.space[3]} />
              <ClinText color="red" variant={TypographyVariant.SmallUI}>
                {addressModalError}
              </ClinText>
            </>
          )}

          <ClinSpacer height={ClinTheme.space[5]} />

          <StyledLoadingContainer>
            <StyledClinPrimaryButton2
              fontSize={ClinTheme.fontSizes[2]}
              type="submit"
              variant="primary"
              disabled={isSubmitting}
            >
              {t('request_new_shipping_address:modal_submit_request')}
            </StyledClinPrimaryButton2>
            {isSubmitting && (
              <StyledLoadingSpinner>
                <ClinSpinner />
              </StyledLoadingSpinner>
            )}
          </StyledLoadingContainer>

          <ClinSpacer height={ClinTheme.space[3]} />

          <StyledClinButton
            display={'block'}
            variant={'secondary'}
            onClick={() => onCancel()}
          >
            {t('common:buttons.cancel')}
          </StyledClinButton>
        </form>
      </StyledDrawerBody>
    </StyledCheckoutDrawer>
  )
}
